import React from 'react';
import IndustryPageTemplate from '../../components/IndustryPageTemplate';
import SEO from '../../components/SEO';

const PetGrooming = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How can Local Business Heroes help my pet grooming business?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Local Business Heroes helps pet grooming businesses capture more leads, automate follow-ups, reduce no-shows, and grow faster. Rated 4.9/5 by 1,200+ UK businesses."
        }
      },
      {
        "@type": "Question",
        "name": "How much does it cost for pet grooming businesses?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Plans start at £297/month with a 14-day trial for just £5. Most pet grooming businesses see ROI within the first month."
        }
      },
      {
        "@type": "Question",
        "name": "Do I need technical skills to use it?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No. Local Business Heroes is designed for busy business owners with zero tech experience. Setup takes 5 minutes with UK-based support."
        }
      },
      {
        "@type": "Question",
        "name": "Will it work for my pet grooming business?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. Local Business Heroes is specifically designed for local service businesses like pet grooming. Features include appointment scheduling, automated reminders, lead capture, and customer management."
        }
      }
    ]
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "CRM for Pet Grooming - Local Business Heroes",
    "applicationCategory": "BusinessApplication",
    "offers": {
      "@type": "Offer",
      "price": "297",
      "priceCurrency": "GBP"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "1200"
    }
  };

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Industries", url: "/industries" },
    { name: "Pet Grooming CRM", url: "/industries/pet-grooming" }
  ];

  const industryData = {
    name: 'Pet Grooming',
    icon: '🐶',
    title: 'Fill Your Grooming Schedule & Build Loyal Pet Parent Relationships',
    subtitle: 'Online booking, automated reminders, and seasonal campaigns that keep appointments full',
    
    challenges: [
      'Last-minute no-shows leaving empty grooming slots',
      'Pet owners forgetting to rebook their dog\'s regular grooming',
      'Spending hours on the phone scheduling and confirming appointments',
      'Seasonal demand spikes (summer) with slow periods in winter',
      'First-time customers not returning for regular grooming',
      'Hard to manage different grooming intervals for different breeds',
      'Mobile grooming makes phone communication difficult',
      'Not enough reviews despite excellent service and happy pets'
    ],
    
    solutions: [
      'Online booking lets pet owners schedule grooming 24/7 from their phone',
      'Automated grooming reminders based on breed-specific intervals (4, 6, or 8 weeks)',
      'SMS reminders reduce no-shows and last-minute cancellations by 75%',
      'Rebook-before-leaving prompts ensure owners schedule next appointment',
      'Seasonal campaigns promote summer coat maintenance and winter de-shedding',
      'Before/after pet photos sent to owners create shareable social proof',
      'Review automation requests feedback after grooming visits',
      'Cancellation waitlist fills empty slots within minutes'
    ],
    
    features: [
      '24/7 Online Booking with Breed-Specific Services',
      'Breed-Based Grooming Interval Reminders (4/6/8 weeks)',
      'Automated SMS & Email Appointment Reminders',
      'Pet Profile Database (Breed, Preferences, Special Needs)',
      'Rebook Automation Before Owner Leaves',
      'Seasonal Grooming Campaign Automation',
      'Before/After Pet Photo Sharing',
      'Cancellation Waitlist Management',
      'Google Review Request After Service',
      'Birthday & Gotcha Day Greetings',
      'Pet Care Tips Email Series',
      'Retail Product Recommendation Campaigns'
    ],
    
    results: [
      '75% reduction in no-shows',
      '88% of pets rebooked before leaving',
      'Fill schedule 95% capacity',
      'Increase regular grooming clients by 60%',
      'Average 4.9★ Google rating',
      'Fill cancellations within 10 minutes'
    ],
    
    reviews: [
      {
        author: 'Emma Richardson',
        location: 'Manchester',
        text: 'The breed-specific reminder system is brilliant. Different dogs need grooming at different intervals and the system tracks it all automatically. My regular client base has grown from 40% to 88%. That\'s predictable appointments.',
        result: '88% regular recurring clients'
      },
      {
        author: 'Sophie Taylor',
        location: 'Birmingham',
        text: 'The no-show reduction has been remarkable. We went from 15-20 no-shows per week to maybe 2. The automated reminders work perfectly and pet owners appreciate them. That\'s £1,200 extra revenue per month.',
        result: '£1,200 additional monthly revenue'
      },
      {
        author: 'Jessica Morgan',
        location: 'Leeds',
        text: 'Online booking has been game-changing for busy pet owners. They can book while watching TV or during lunch breaks. 70% of my bookings now come online with no phone calls. My phone barely rings during grooming hours.',
        result: '70% of bookings now online'
      },
      {
        author: 'Amy Williams',
        location: 'Bristol',
        text: 'The before/after pet photos have been incredible for marketing. Owners love getting photos of their freshly groomed dogs and they share them with friends. I\'ve gotten 45 new clients from photo shares alone in 6 months.',
        result: '45 new clients from photo sharing'
      },
      {
        author: 'Rachel Bennett',
        location: 'Liverpool',
        text: 'The review automation got us from 18 reviews to 134 in 8 months. We\'re the top-rated groomer in Liverpool. New clients specifically mention our reviews and the photos we send. Bookings are consistently full 2 weeks ahead.',
        result: '134 reviews, fully booked 2 weeks ahead'
      },
      {
        author: 'Laura Foster',
        location: 'Glasgow',
        text: 'The rebook-before-leaving feature ensures owners schedule their next grooming before walking out. My retention went from 65% to 88%. That\'s consistent work and I can plan my schedule confidently.',
        result: '88% client retention rate'
      }
    ]
  };

  return (
    <>
      <SEO 
        title="CRM for Pet Grooming UK | Marketing Automation | Local Business Heroes"
        description="Rated 4.9/5. CRM and marketing automation for pet grooming businesses. Capture more leads, automate follow-up, grow faster. Start £5 trial today."
        keywords="CRM for pet grooming UK, pet grooming business software, pet grooming customer management, pet grooming lead generation UK"
        url="/industries/pet-grooming"
        schemaMarkup={[faqSchema, localBusinessSchema]}
        breadcrumbs={breadcrumbs}
      />
      <IndustryPageTemplate industry={industryData} />
    </>
  );
};

export default PetGrooming;